import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FigmaTypography } from "@libs/zapi-ui/custom";
import i18n, { projectLanguageToLng } from "@/zapi-frontend/shared/i18n";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import {
  AiMessage,
  ChatShell,
  ProjectGenerationProgress,
  TypingDots,
  UserMessage
} from "@/zapi-frontend/shared/ui/chat";
import type { TGenerationProgressProps } from "@/zapi-frontend/widgets/ChatIsland/ui/ChatMessageList";
import { MessageMarkdown } from "@/zapi-frontend/shared/ui/custom/ChatMessage/MessageMarkdown";
import { isLeoChangingContent, type TLeoChatMessage } from "@/zapi-frontend/stores/leoChatStore";
import { useModelLedger } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/ModelLedgerIsland/useModelLedger";
import { FigureRefsProvider } from "@/zapi-frontend/pages/ProjectDetailsPage/ui/MemoIsland/FigureRefs/FigureRefsProvider";
import { useLeoChat } from "../hooks/useLeoChat";
import { useLeoImages } from "../hooks/useLeoImages";
import { isFollowUpLatest, readerMessages, replyMarkdown, replySlots } from "../lib/leoHistory";
import { LeoBusyMessage } from "./LeoBusyMessage";
import { LeoAttachImageButton, LeoMessageImages, LeoStagedImages } from "./LeoImages";
import { LeoWelcome } from "./LeoWelcome";
import { AnalysisWaiting, useAnalysisWaiting } from "./AnalysisWaiting";

type TLeoChatProps = {
  projectId: string;
  headerRight?: ReactNode;
  canEdit?: boolean;
  slideIndex?: number;
  generationProgress?: TGenerationProgressProps;
};

function KeyedReply({ projectId, message }: { projectId: string; message: TLeoChatMessage }) {
  const ledger = useModelLedger(projectId);
  const figures = ledger.data?.figures;
  const byKey = useMemo(() => new Map((figures ?? []).map((figure) => [figure.key, figure])), [figures]);
  if (ledger.isLoading) return <TypingDots />;
  return (
    <FigureRefsProvider figures={figures}>
      <MessageMarkdown content={replyMarkdown(message, byKey)} />
    </FigureRefsProvider>
  );
}

// The slides a turn changed, kept under its reply.
function UpdatedSlides({ slides }: { slides: number[] }) {
  const { t } = useTranslation("projectHome");
  return (
    <FigmaTypography variant="body-3" color="zinc500">
      {t("leo.updatedSlides", { count: slides.length, slides: slides.join(", ") })}
    </FigmaTypography>
  );
}

export function LeoChat({ projectId, headerRight, canEdit = true, slideIndex, generationProgress }: TLeoChatProps) {
  const { t } = useTranslation("projectHome");
  const projectLanguage = useProjectStore((state) => state.selectedProject?.language);
  const busyNotice = useMemo(
    () => i18n.getFixedT(projectLanguageToLng(projectLanguage), "projectHome")("leo.busy"),
    [projectLanguage]
  );
  const {
    messages,
    activeTask,
    isBusy,
    status,
    send,
    stop,
    clear
  } = useLeoChat(projectId, {
    canEdit,
    slideIndex
  });

  const isGenerating = !!generationProgress;
  const isWaiting = !!generationProgress?.isSettingUp;
  const waiting = useAnalysisWaiting(projectId, isWaiting, generationProgress?.setup ?? null);
  const isChangingContent = isLeoChangingContent(activeTask);

  const [input, setInput] = useState("");
  const images = useLeoImages();
  const scrollRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, isBusy, status, isChangingContent, waiting.exchanges]);

  const submit = async () => {
    const text = input.trim();
    if (isWaiting) {
      if (!text) return;
      setInput("");
      waiting.reply(text);
      return;
    }
    if ((!text && images.staged.length === 0) || isBusy || isGenerating) return;
    setInput("");
    const taken = images.take();
    const unsent = await send(text, taken);
    if (unsent === null) return;
    setInput(unsent);
    images.add(taken.map((image) => image.file));
  };
  const canAttach = !isBusy && !isGenerating && !images.isFull;

  return (
    <ChatShell
      appearance="leo"
      data-testid="leo-chat-panel"
      className="h-full w-full"
      title={t("leo.title")}
      headerRight={headerRight}
      placeholder={isGenerating && !isWaiting ? t("leo.placeholderGenerating") : t("leo.placeholderAnything")}
      input={input}
      onInputChange={setInput}
      onSubmit={() => void submit()}
      canSubmit={input.trim().length > 0 || images.staged.length > 0}
      inputExtras={<LeoStagedImages images={images.staged} onRemove={images.remove} />}
      controlsLeft={<LeoAttachImageButton onFiles={images.add} disabled={!canAttach} />}
      onPasteFiles={canAttach ? images.add : undefined}
      onDropFiles={canAttach ? images.add : undefined}
      submitLabel={t("leo.send")}
      clearConfirm={{
        menuLabel: t("leo.clear"),
        title: t("leo.clearTitle"),
        cancelText: t("leo.cancel"),
        confirmText: t("leo.clear"),
        toastText: t("leo.chatCleared")
      }}
      isBusy={isBusy}
      isComposerDisabled={isGenerating && !isWaiting}
      onStop={() => void stop()}
      stopTestId="leo-chat-stop"
      inputTestId="leo-chat-input"
      sendTestId="leo-chat-send"
      clearTestId="leo-chat-clear"
      onClear={clear}
      canClear={messages.length > 0 && !isBusy}
      scrollRef={scrollRef}
      footnote={t("leo.disclaimer")}
      busyNotice={busyNotice}
      dockCloseLabel={t("leo.hide")}
    >
      {isWaiting ? (
        <AnalysisWaiting exchanges={waiting.exchanges} onAsk={waiting.ask} />
      ) : generationProgress ? (
        <ProjectGenerationProgress {...generationProgress} />
      ) : (
        messages.length === 0 && (
          <LeoWelcome
            title={t("leo.welcomeTitle")}
            subtitle={t("leo.welcomeSubtitle")}
            suggestions={[t("leo.suggestionStands"), t("leo.suggestionUnproven"), t("leo.suggestionNext")]}
            onPick={(suggestion) => void send(suggestion)}
            disabled={isBusy}
          />
        )
      )}

      {readerMessages(isWaiting ? [] : messages).map(({ message, chaptersOnly }, index) => {
        if (chaptersOnly) {
          return (
            <AiMessage key={index} className="w-full">
              {!!message.appliedChapters?.length && (
                <FigmaTypography variant="body-3" color="zinc500">
                  {t("leo.updatedChapters", { chapters: message.appliedChapters.join(", ") })}
                </FigmaTypography>
              )}
              {!!message.appliedSlides?.length && <UpdatedSlides slides={message.appliedSlides} />}
            </AiMessage>
          );
        }
        if (message.role === "user") {
          return (
            <UserMessage key={index}>
              {!!message.images?.length && <LeoMessageImages images={message.images} />}
              {message.content && <span className="whitespace-pre-wrap">{message.content}</span>}
            </UserMessage>
          );
        }
        return (
          <AiMessage key={index} state={message.isError ? "error" : "default"} className="w-full">
            {replySlots(message) ? (
              <KeyedReply projectId={projectId} message={message} />
            ) : (
              <MessageMarkdown content={message.content} />
            )}
            {!!message.appliedChapters?.length && (
              <FigmaTypography variant="body-3" color="zinc500">
                {t("leo.updatedChapters", { chapters: message.appliedChapters.join(", ") })}
              </FigmaTypography>
            )}
            {!!message.appliedSlides?.length && <UpdatedSlides slides={message.appliedSlides} />}
          </AiMessage>
        );
      })}

      {!isWaiting && isBusy && !isFollowUpLatest(messages) && (
        <LeoBusyMessage status={status} isChangingContent={isChangingContent} />
      )}
    </ChatShell>
  );
}
